/**
 * Appointments TanStack Query Hook (T066)
 * Real-time polling for new appointments
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient, { type ApiResponse } from '../lib/api-client';

export interface Appointment {
  id: string;
  customerId: string;
  customerName: string;
  customerEmail: string;
  title: string;
  description?: string;
  startTime: string;
  endTime: string;
  duration: number;
  status: 'SCHEDULED' | 'CONFIRMED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW';
  location?: string;
  meetingUrl?: string;
  zoomMeetingId?: string | null;
  acuityId?: string | null;
  notes?: string;
  cancelledAt?: string;
  cancelledReason?: string;
  hasRecording?: boolean;
  hasInvoice?: boolean;
  createdAt: string;
  updatedAt: string;
  customer?: {
    id: string;
    name: string;
    email: string;
    phone: string | null;
  };
  recording?: {
    id: string;
    videoUrl?: string;
    duration?: number;
  } | null;
  invoice?: {
    id: string;
    invoiceNumber: string;
    total: number;
    status: string;
  } | null;
}

export interface AppointmentsResponse {
  appointments: Appointment[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export interface AppointmentFilters {
  search?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  customerId?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export function useAppointments(filters: AppointmentFilters = {}) {
  return useQuery({
    queryKey: ['appointments', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.search) {
        params.append('search', filters.search);
      }
      if (filters.status && filters.status !== 'all') {
        params.append('status', filters.status);
      }
      if (filters.startDate) {
        params.append('startDate', filters.startDate);
      }
      if (filters.endDate) {
        params.append('endDate', filters.endDate);
      }
      if (filters.customerId) {
        params.append('customerId', filters.customerId);
      }
      if (filters.page) {
        params.append('page', filters.page.toString());
      }
      if (filters.limit) {
        params.append('limit', filters.limit.toString());
      }
      if (filters.sortBy) {
        params.append('sortBy', filters.sortBy);
      }
      if (filters.sortOrder) {
        params.append('sortOrder', filters.sortOrder);
      }

      const response = await apiClient.get<ApiResponse<AppointmentsResponse>>(
        `/appointments?${params.toString()}`
      );
      return response.data.data;
    },
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function useAppointment(id: string | null) {
  return useQuery({
    queryKey: ['appointment', id],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Appointment>>(`/appointments/${id}`);
      return response.data.data;
    },
    enabled: !!id,
  });
}

export function useBulkUpdateStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { appointmentIds: string[]; status: string; reason?: string }) => {
      const response = await apiClient.patch<ApiResponse<{ updated: number; status: string }>>(
        '/appointments/bulk/status',
        data
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
    },
  });
}

export function useUpdateAppointmentStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { id: string; status: string; cancelledReason?: string }) => {
      const response = await apiClient.patch<ApiResponse<Appointment>>(
        `/appointments/${data.id}/status`,
        { status: data.status, cancelledReason: data.cancelledReason }
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      queryClient.invalidateQueries({ queryKey: ['appointment'] });
    },
  });
}
